import { useEffect, useRef, useState } from "react";

const advantages = [
  {
    icon: "ri-team-line",
    title: "No need to hire local teams",
    desc: "Skip the 12-month hiring cycle. We become your dedicated, embedded sales presence from day one.",
  },
  {
    icon: "ri-rocket-line",
    title: "Faster market entry",
    desc: "While competitors spend years establishing trust, we activate existing networks and proven playbooks to accelerate your entry.",
  },
  {
    icon: "ri-robot-line",
    title: "AI-powered follow-up",
    desc: "Zero lost leads. Our automated sequences maintain precise, contextual contact until every prospect converts or opts out.",
  },
  {
    icon: "ri-map-pin-line",
    title: "Real local expertise",
    desc: "Native fluency in Romanian, Moldovan and regional business cultures. Every nuance understood, every relationship respected.",
  },
  {
    icon: "ri-eye-line",
    title: "Full pipeline transparency",
    desc: "Real-time dashboards. Weekly reports. You always know exactly where your deals stand and what happens next.",
  },
  {
    icon: "ri-award-line",
    title: "Performance-driven model",
    desc: "Our success is measured by your results. We bring skin in the game to every engagement we take on.",
  },
];

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function Differentiation() {
  const { ref, inView } = useInView();

  return (
    <section id="differentiation" className="bg-[#080808] py-32 px-6 relative overflow-hidden">
      {/* Large decorative text */}
      <div className="absolute left-0 top-1/2 -translate-y-1/2 font-['Cormorant_Garamond'] text-[18vw] font-light text-white/[0.018] leading-none select-none pointer-events-none whitespace-nowrap">
        EMCALU
      </div>

      <div className="max-w-7xl mx-auto relative">
        {/* Header */}
        <div ref={ref} className={`text-center mb-20 transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-16 h-px bg-[#C9A84C]/40" />
            <span className="text-[#C9A84C] text-xs tracking-[0.3em] uppercase font-light font-['Outfit']">Our Edge</span>
            <div className="w-16 h-px bg-[#C9A84C]/40" />
          </div>
          <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl lg:text-6xl font-light text-white">
            Why companies choose <em className="text-[#C9A84C] not-italic">EmCaLu</em>
          </h2>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#C9A84C]/8">
          {advantages.map((adv, i) => (
            <div
              key={adv.title}
              className={`bg-[#080808] p-10 group hover:bg-[#0d0d0d] transition-all duration-500 cursor-default ${
                inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              {/* Icon */}
              <div className="w-10 h-10 flex items-center justify-center border border-[#C9A84C]/15 group-hover:border-[#C9A84C]/50 transition-all duration-300 mb-7">
                <i className={`${adv.icon} text-[#C9A84C]/50 group-hover:text-[#C9A84C] text-lg transition-colors duration-300`} />
              </div>

              {/* Content */}
              <h3 className="font-['Cormorant_Garamond'] text-xl lg:text-2xl font-light text-white mb-3 group-hover:text-white transition-colors duration-300">
                {adv.title}
              </h3>
              <p className="text-white/40 text-sm font-light leading-relaxed font-['Outfit'] group-hover:text-white/55 transition-colors duration-300">
                {adv.desc}
              </p>

              {/* Bottom accent */}
              <div className="mt-6 w-0 h-px bg-[#C9A84C] group-hover:w-full transition-all duration-500" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
